import {
  AccountInfo,
  AuthenticationResult,
  Configuration,
  InteractionRequiredAuthError,
  LogLevel,
  PublicClientApplication,
  RedirectRequest,
} from "@azure/msal-browser";
import { User as DtoUser } from "@casablanca/casablanca.contracts.iam-client-ts-v1/dist/models/User";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import getAzureApplicationInsights from "../plugins/applicationInsights";
import { User } from "../types/globals";

import userApi from "./apis/iam/userApi";
import { env } from "./environment";

// User cache to prevent frequent API calls
interface UserCache {
  user: User;
  accountId: string;
  timestamp: number;
  expiresAt: number;
}

let userCache: UserCache | null = null;
let userCacheDuration = 5 * 60 * 1000; // 5 minutes in milliseconds

// Function to get cache status
function getUserCacheStatus(): {
  isCached: boolean;
  timeUntilExpiry: number;
  accountId: string | null;
} {
  if (!userCache) {
    return { isCached: false, timeUntilExpiry: 0, accountId: null };
  }

  const now = Date.now();
  const timeUntilExpiry = Math.max(0, userCache.expiresAt - now);

  return {
    isCached: timeUntilExpiry > 0,
    timeUntilExpiry,
    accountId: userCache.accountId,
  };
}

// Function to set cache duration (useful for testing or dynamic adjustment)
function setUserCacheDuration(durationMs: number): void {
  userCacheDuration = durationMs;
  // Clear existing cache when changing duration
  if (userCache) {
    userCache.expiresAt = userCache.timestamp + durationMs;
  }
}

// Function to handle account changes (useful for multi-account scenarios)
function handleAccountChange(newAccountId: string): void {
  if (userCache && userCache.accountId !== newAccountId) {
    // Different account, clear cache
    clearUserCache();
  }
}

/* Converts application Insight severity level to msal log level */
const getMsalLogLevel = () => {
  switch (env.MINIMUM_LOGLEVEL) {
    case SeverityLevel.Verbose:
      return LogLevel.Verbose;
    case SeverityLevel.Information:
      return LogLevel.Info;
    case SeverityLevel.Warning:
      return LogLevel.Warning;
    case SeverityLevel.Error:
    case SeverityLevel.Critical:
      return LogLevel.Error;
    default:
      return LogLevel.Verbose;
  }
};

const getMsalConfig = async () => {
  const appInsights = await getAzureApplicationInsights();
  const msalConfig: Configuration = {
    auth: {
      clientId: env.B2C_CLIENT_ID,
      authority: env.B2C_AUTHORITY,
      redirectUri: window.location.origin,
      knownAuthorities: [env.B2C_KNOWN_AUTHORITY],
    },
    cache: {
      cacheLocation: "localStorage", // Can be 'localStorage' or 'sessionStorage' (sessionStorage is more secure against XSS, but the user has to relogin for every new tab)
      storeAuthStateInCookie: false, // Set to true if needed (for older browsers)
    },
    system: {
      loggerOptions: {
        logLevel: getMsalLogLevel(),
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              appInsights.trackException({ exception: new Error(message) });
              return;
            case LogLevel.Info:
              appInsights.trackTrace({
                message: message,
                severityLevel: SeverityLevel.Information,
              });
              return;
            case LogLevel.Verbose:
              appInsights.trackTrace({
                message: message,
                severityLevel: SeverityLevel.Verbose,
              });
              return;
            case LogLevel.Warning:
              appInsights.trackTrace({
                message: message,
                severityLevel: SeverityLevel.Warning,
              });
              return;
            default:
              return;
          }
        },
        piiLoggingEnabled: false, // set to true if you want to log personal data
      },
    },
  };
  return msalConfig;
};

let msalInstance: PublicClientApplication | null = null;
const getMsalInstance = async () => {
  if (msalInstance) {
    return msalInstance;
  }
  await initMsalInstance();
  return msalInstance;
};

const initMsalInstance = async () => {
  if (!msalInstance) {
    const msalConfig = await getMsalConfig();
    msalInstance = new PublicClientApplication(msalConfig);
  }
};

async function getCurrentAccount(): Promise<AccountInfo> {
  await initMsalInstance();
  const account = msalInstance.getActiveAccount();
  if (!account) {
    // Persist the full query string across the B2C redirect so params like
    // ?userflow= survive the round-trip.
    if (window.location.search) {
      sessionStorage.setItem("pendingSearch", window.location.search);
    }
    await msalInstance.loginRedirect({
      // or loginPopup
      scopes: ["openid", "profile", env.B2C_SCOPE],
    });
    return null;
  }
  return account;
}

async function acquireToken(): Promise<string | null> {
  await initMsalInstance();
  const account = await getCurrentAccount();

  const request = {
    scopes: ["openid", "profile", env.B2C_SCOPE],
    account: account,
  } as RedirectRequest;

  try {
    const response: AuthenticationResult =
      await msalInstance.acquireTokenSilent(request);
    msalInstance.setActiveAccount(response.account);
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Persist the full query string so params like ?userflow= survive the
      // interactive token redirect.
      if (window.location.search) {
        sessionStorage.setItem("pendingSearch", window.location.search);
      }
      await msalInstance.acquireTokenRedirect(request); // or acquireTokenPopup
    }
    throw error;
  }
}

async function logout(): Promise<void> {
  await initMsalInstance();
  clearUserCache();
  await msalInstance.logoutRedirect();
}

async function getCurrentUser(): Promise<User | null> {
  await initMsalInstance();
  const account = msalInstance.getActiveAccount();

  if (!account) {
    return null;
  }

  const currentAccountId = account.idTokenClaims.oid as string;

  // Handle account changes
  handleAccountChange(currentAccountId);

  // Check if we have a valid cached user for the current account
  if (
    userCache &&
    userCache.accountId === currentAccountId &&
    Date.now() < userCache.expiresAt
  ) {
    return userCache.user;
  }

  // Cache miss or expired, fetch from API
  let user: DtoUser;
  try {
    user = await userApi.getUserByObjectId(currentAccountId);
  } catch (error) {
    error.message += ` when getting user with objectId: ${currentAccountId}`;
    throw error;
  }

  if (user) {
    const userData: User = {
      id: user.id as string,
      b2cObjectId: account.idTokenClaims.oid as string,
      firstName: account.idTokenClaims.userFirstName as string,
      lastName: account.idTokenClaims.userLastName as string,
      email: account.idTokenClaims.email as string,
      jobTitle: account.idTokenClaims.userJobTitle as string,
      zipCode: account.idTokenClaims.userAddressZipCode as string,
      street: account.idTokenClaims.userAddressStreet as string,
      country: account.idTokenClaims.userAddressCountry as string,
      city: account.idTokenClaims.userAddressCity as string,
    };

    // Update cache
    userCache = {
      user: userData,
      accountId: currentAccountId,
      timestamp: Date.now(),
      expiresAt: Date.now() + userCacheDuration,
    };

    return userData;
  }

  return null;
}

// Function to clear user cache (useful for logout or when user data changes)
function clearUserCache(): void {
  userCache = null;
}

// Function to force refresh user data (bypasses cache)
async function refreshUserData(): Promise<User | null> {
  clearUserCache();
  return await getCurrentUser();
}

async function resetPassword() {
  await initMsalInstance();
  const loginHint = await getLoginHint();
  await msalInstance.loginRedirect({
    authority: env.B2C_PASSWORD_RESET_AUTHORITY,
    scopes: ["openid", "profile", env.B2C_SCOPE],
    redirectUri: window.location.origin,
    loginHint: loginHint,
  });
}

async function getLoginHint() {
  const appInsights = await getAzureApplicationInsights();
  let loginHint: string | undefined;
  const account = msalInstance.getActiveAccount();
  if (account) {
    if (account?.username) {
      loginHint = account.username;
      appInsights.trackTrace({
        message: `Using loginHint from account.username: ${loginHint}`,
        severityLevel: SeverityLevel.Information,
      });
    } else if (account?.idTokenClaims?.email) {
      loginHint = account.idTokenClaims.email as string;
      appInsights.trackTrace({
        message: `Using loginHint from idTokenClaims.email: ${loginHint}`,
        severityLevel: SeverityLevel.Information,
      });
    } else {
      appInsights.trackTrace({
        message:
          "Account found, but no usable loginHint (username or email) found.",
        severityLevel: SeverityLevel.Warning,
      });
    }
  } else {
    appInsights.trackTrace({
      message: "No accounts found, loginHint will be omitted.",
      severityLevel: SeverityLevel.Warning,
    });
  }
  return loginHint;
}

async function manageMfa() {
  await initMsalInstance();
  const loginHint = await getLoginHint();
  await msalInstance.loginRedirect({
    authority: env.B2C_MFA_MANAGEMENT_AUTHORITY,
    scopes: ["openid", "profile", env.B2C_SCOPE],
    redirectUri: window.location.origin,
    loginHint: loginHint,
  });
}

window.logout = logout;
window.acquireToken = acquireToken;
window.getCurrentUser = getCurrentUser;
window.resetPassword = resetPassword;
window.manageMfa = manageMfa;
window.clearUserCache = clearUserCache;
window.refreshUserData = refreshUserData;
window.getUserCacheStatus = getUserCacheStatus;
window.setUserCacheDuration = setUserCacheDuration;
window.handleAccountChange = handleAccountChange;

export {
  acquireToken,
  clearUserCache,
  getCurrentAccount,
  getCurrentUser,
  getMsalInstance,
  getUserCacheStatus,
  handleAccountChange,
  logout,
  manageMfa,
  refreshUserData,
  setUserCacheDuration,
};
